import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDollarSign, FiDownload, FiPrinter, FiSearch, FiTrendingUp,
  FiPercent, FiBarChart2, FiUsers, FiMapPin, FiShoppingCart
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#4A90D9',
  '#E67E22',
  '#2ECC71',
  '#9B59B6'
];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getGroupKey = (dateStr, groupBy) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  switch (groupBy) {
    case 'daily':
      return dateStr;
    case 'weekly': {
      const startOfYear = new Date(date.getFullYear(), 0, 1);
      const diff = date - startOfYear;
      const oneWeek = 604800000;
      const weekNum = Math.ceil((diff / oneWeek) + 1);
      return `${date.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
    }
    case 'monthly':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    case 'quarterly': {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}-Q${quarter}`;
    }
    default:
      return dateStr;
  }
};

const getGroupLabel = (key, groupBy) => {
  if (!key || key === 'Unknown') return 'Unknown';
  switch (groupBy) {
    case 'daily':
      return formatDateStr(key);
    case 'weekly':
      return key;
    case 'monthly': {
      const [year, month] = key.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    }
    case 'quarterly':
      return key;
    default:
      return key;
  }
};

const SalesIntelligenceReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stockFlowData, setStockFlowData] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [groupBy, setGroupBy] = useState('monthly');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState('all');
  const [selectedAreaId, setSelectedAreaId] = useState('all');
  const [taxFilter, setTaxFilter] = useState('both');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const custRef = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(custRef, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // Load regions
  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  // Load areas
  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadCustomers();
    loadRegions();
    loadAreas();

    // Set default date range
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, [loadFinancialYears, loadCustomers, loadRegions, loadAreas]);

  // Load all data
  const loadData = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasSearched(true);

    try {
      // Fetch transactions (QT, SO, SI, SR)
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const transQuery = query(
        transRef,
        where('tranTypeCode', 'in', ['QT', 'SO', 'SI', 'NSI', 'PS', 'SR', 'NSR']),
        where('financialYearId', '==', selectedFYId)
      );
      const transSnapshot = await getDocs(transQuery);
      let allTransactions = transSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      allTransactions.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setTransactions(allTransactions);

      // Fetch stockFlow for product-level data
      const sfRef = collection(db, 'companies', activeCompany.id, 'stockFlow');
      const sfQuery = query(
        sfRef,
        where('status', '==', 'Posted')
      );
      const sfSnapshot = await getDocs(sfQuery);
      setStockFlowData(sfSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        // Same financial year as the transactions query above — without this the
        // stock side counted every year and doubled the product figures.
        .filter(t => !t.financialYearId || t.financialYearId === selectedFYId)
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        }));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build customer lookup
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => {
      map[c.id] = c;
    });
    return map;
  }, [customers]);

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }
    if (selectedCustomer) {
      filtered = filtered.filter(t => t.partyId === selectedCustomer);
    }
    if (selectedRegionId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.regionId === selectedRegionId;
      });
    }
    if (selectedAreaId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.areaId === selectedAreaId;
      });
    }
    if (taxFilter === 'tax') {
      filtered = filtered.filter(t => !['NSI', 'NSR'].includes(t.tranTypeCode));
    } else if (taxFilter === 'nonTax') {
      filtered = filtered.filter(t => !['SI', 'SR'].includes(t.tranTypeCode));
    }

    return filtered;
  }, [transactions, dateFrom, dateTo, selectedCustomer, selectedRegionId, selectedAreaId, customerMap, taxFilter]);

  // Filtered stockFlow
  const filteredStockFlow = useMemo(() => {
    let filtered = stockFlowData.filter(
      sf => ['SI', 'NSI', 'PS'].includes(sf.tranTypeCode) && sf.direction === 'out'
    );
    if (dateFrom) {
      filtered = filtered.filter(sf => (sf.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(sf => (sf.docDate || '') <= dateTo);
    }
    if (selectedCustomer) {
      filtered = filtered.filter(sf => sf.partyId === selectedCustomer);
    }
    if (selectedRegionId !== 'all') {
      filtered = filtered.filter(sf => {
        const cust = customerMap[sf.partyId];
        return cust && cust.regionId === selectedRegionId;
      });
    }
    if (selectedAreaId !== 'all') {
      filtered = filtered.filter(sf => {
        const cust = customerMap[sf.partyId];
        return cust && cust.areaId === selectedAreaId;
      });
    }
    return filtered;
  }, [stockFlowData, dateFrom, dateTo, selectedCustomer, selectedRegionId, selectedAreaId, customerMap]);

  // Funnel computation
  const funnelData = useMemo(() => {
    const qt = filteredTransactions.filter(t => t.tranTypeCode === 'QT');
    const qtAccepted = qt.filter(t => ['Accepted', 'Sent'].includes(t.status));
    const so = filteredTransactions.filter(t => t.tranTypeCode === 'SO');
    const soApproved = so.filter(t => ['Approved', 'Completed', 'Partially Delivered', 'Closed'].includes(t.status));
    const si = filteredTransactions.filter(t => (['SI', 'NSI', 'PS'].includes(t.tranTypeCode)) && t.status === 'Posted');

    const qtCount = qt.length;
    const qtValue = qt.reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0);
    const qtAccCount = qtAccepted.length;
    const qtAccValue = qtAccepted.reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0);
    const soCount = so.length;
    const soValue = so.reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0);
    const soAppCount = soApproved.length;
    const soAppValue = soApproved.reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0);
    const siCount = si.length;
    const siValue = si.reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0);

    return {
      stages: [
        { stage: 'Quotations', count: qtCount, value: qtValue, conversion: 100 },
        { stage: 'QT Accepted', count: qtAccCount, value: qtAccValue, conversion: qtCount > 0 ? ((qtAccCount / qtCount) * 100) : 0 },
        { stage: 'Sales Orders', count: soCount, value: soValue, conversion: qtAccCount > 0 ? ((soCount / qtAccCount) * 100) : 0 },
        { stage: 'SO Approved', count: soAppCount, value: soAppValue, conversion: soCount > 0 ? ((soAppCount / soCount) * 100) : 0 },
        { stage: 'Invoiced', count: siCount, value: siValue, conversion: soAppCount > 0 ? ((siCount / soAppCount) * 100) : 0 }
      ],
      qtCount, qtValue, qtAccCount, siCount, siValue
    };
  }, [filteredTransactions]);

  // Summary calculations
  const summary = useMemo(() => {
    const { qtCount, qtValue, qtAccCount, siCount, siValue } = funnelData;
    const conversionRate = qtCount > 0 ? ((qtAccCount / qtCount) * 100) : 0;

    // Unique customers from SI
    const siTransactions = filteredTransactions.filter(t => (['SI', 'NSI', 'PS'].includes(t.tranTypeCode)) && t.status === 'Posted');
    const uniqueCustomerIds = new Set(siTransactions.map(t => t.partyId).filter(Boolean));
    const uniqueCustomers = uniqueCustomerIds.size;

    const avgOrderValue = siCount > 0 ? (siValue / siCount) : 0;

    // Top region by revenue
    const regionRevenue = {};
    siTransactions.forEach(t => {
      const cust = customerMap[t.partyId];
      const region = cust?.city || cust?.area || cust?.state || 'Unknown';
      regionRevenue[region] = (regionRevenue[region] || 0) + (Number(t.totalAmount) || 0);
    });

    let topRegion = 'N/A';
    let topRegionRevenue = 0;
    Object.entries(regionRevenue).forEach(([region, revenue]) => {
      if (revenue > topRegionRevenue) {
        topRegion = region;
        topRegionRevenue = revenue;
      }
    });

    return {
      qtCount,
      qtValue,
      conversionRate,
      totalRevenue: siValue,
      topRegion,
      topRegionRevenue,
      uniqueCustomers,
      avgOrderValue
    };
  }, [funnelData, filteredTransactions, customerMap]);

  // Sales Funnel chart data (horizontal bar)
  const funnelChartData = useMemo(() => {
    return [...funnelData.stages].reverse();
  }, [funnelData]);

  // Revenue by Region chart data
  const regionChartData = useMemo(() => {
    const siTransactions = filteredTransactions.filter(t => (['SI', 'NSI', 'PS'].includes(t.tranTypeCode)) && t.status === 'Posted');
    const regionRevenue = {};

    siTransactions.forEach(t => {
      const cust = customerMap[t.partyId];
      const region = cust?.city || cust?.area || cust?.state || 'Unknown';
      regionRevenue[region] = (regionRevenue[region] || 0) + (Number(t.totalAmount) || 0);
    });

    return Object.entries(regionRevenue)
      .map(([region, revenue]) => ({ region, revenue }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  }, [filteredTransactions, customerMap]);

  // Top Products chart data
  const topProductsData = useMemo(() => {
    const productRevenue = {};

    filteredStockFlow.forEach(sf => {
      const key = sf.itemId || sf.itemName || 'Unknown';
      if (!productRevenue[key]) {
        productRevenue[key] = {
          name: sf.itemName || sf.itemCode || key,
          units: 0,
          revenue: 0,
          cost: 0
        };
      }
      // Stock rows carry quantity / netAmount / totalCost (INV-04: the cost is
      // the weighted average the sale took out of stock).
      productRevenue[key].units += Number(sf.qty) || Number(sf.quantity) || 0;
      productRevenue[key].revenue += Number(sf.amount) || Number(sf.totalAmount) || Number(sf.netAmount) || 0;
      productRevenue[key].cost += Number(sf.costAmount) || Number(sf.totalCost) || 0;
    });

    return Object.values(productRevenue)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10)
      .map((p, i) => ({
        ...p,
        rank: i + 1,
        margin: p.revenue > 0 ? (((p.revenue - p.cost) / p.revenue) * 100) : 0
      }));
  }, [filteredStockFlow]);

  // Customer Acquisition area chart (monthly new customers)
  const acquisitionData = useMemo(() => {
    const siTransactions = filteredTransactions.filter(t => (['SI', 'NSI', 'PS'].includes(t.tranTypeCode)) && t.status === 'Posted');
    // Track first appearance of each customer
    const customerFirstSeen = {};
    const sortedSI = [...siTransactions].sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));

    sortedSI.forEach(t => {
      if (t.partyId && !customerFirstSeen[t.partyId]) {
        customerFirstSeen[t.partyId] = t.docDate;
      }
    });

    // Group by month
    const monthlyNew = {};
    Object.values(customerFirstSeen).forEach(dateStr => {
      const key = getGroupKey(dateStr, 'monthly');
      monthlyNew[key] = (monthlyNew[key] || 0) + 1;
    });

    return Object.entries(monthlyNew)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, count]) => ({
        period,
        label: getGroupLabel(period, 'monthly'),
        newCustomers: count
      }));
  }, [filteredTransactions]);

  // Region table data
  const regionTableData = useMemo(() => {
    const totalRevenue = regionChartData.reduce((sum, r) => sum + r.revenue, 0);
    return regionChartData.map(r => ({
      ...r,
      pctOfTotal: totalRevenue > 0 ? ((r.revenue / totalRevenue) * 100) : 0,
      growth: 0 // Growth would require previous period data
    }));
  }, [regionChartData]);

  // CSV Export
  const handleExport = () => {
    if (!hasSearched) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Sales Intelligence Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;

    // Summary
    csv += 'SUMMARY\n';
    csv += `Total Quotations,${summary.qtCount},${summary.qtValue}\n`;
    csv += `Conversion Rate,${summary.conversionRate.toFixed(1)}%\n`;
    csv += `Total Revenue,${summary.totalRevenue}\n`;
    csv += `Top Region,${summary.topRegion},${summary.topRegionRevenue}\n`;
    csv += `Unique Customers,${summary.uniqueCustomers}\n`;
    csv += `Avg Order Value,${summary.avgOrderValue}\n\n`;

    // Funnel
    csv += 'SALES FUNNEL\n';
    csv += 'Stage,Count,Value,Conversion %\n';
    funnelData.stages.forEach(s => {
      csv += `"${s.stage}",${s.count},${s.value},${s.conversion.toFixed(1)}%\n`;
    });
    csv += '\n';

    // Top Products
    csv += 'TOP PRODUCTS\n';
    csv += 'Rank,Product,Units,Revenue,Margin %\n';
    topProductsData.forEach(p => {
      csv += `${p.rank},"${(p.name || '').replace(/"/g, '""')}",${p.units},${p.revenue},${p.margin.toFixed(1)}%\n`;
    });
    csv += '\n';

    // Regions
    csv += 'REGIONS\n';
    csv += 'Region,Revenue,% of Total,Growth %\n';
    regionTableData.forEach(r => {
      csv += `"${(r.region || '').replace(/"/g, '""')}",${r.revenue},${r.pctOfTotal.toFixed(1)}%,${r.growth.toFixed(1)}%\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Sales_Intelligence_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Sales Intelligence Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        h2{color:#800020;font-size:16px;margin:20px 0 10px 0}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Sales Intelligence Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total Quotations</div><div class="summary-val">${summary.qtCount} (${formatAmount(summary.qtValue)})</div></div>
        <div class="summary-box"><div class="summary-label">Conversion Rate</div><div class="summary-val">${summary.conversionRate.toFixed(1)}%</div></div>
        <div class="summary-box"><div class="summary-label">Total Revenue</div><div class="summary-val">${formatAmount(summary.totalRevenue)}</div></div>
        <div class="summary-box"><div class="summary-label">Top Region</div><div class="summary-val">${summary.topRegion}</div></div>
        <div class="summary-box"><div class="summary-label">Unique Customers</div><div class="summary-val">${summary.uniqueCustomers}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Order Value</div><div class="summary-val">${formatAmount(summary.avgOrderValue)}</div></div>
      </div>
      <h2>Sales Funnel</h2>
      <table>
        <thead><tr><th>Stage</th><th class="right">Count</th><th class="right">Value</th><th class="right">Conversion %</th></tr></thead>
        <tbody>
          ${funnelData.stages.map(s => `
            <tr><td>${s.stage}</td><td class="right">${s.count}</td><td class="right">${formatAmount(s.value)}</td><td class="right">${s.conversion.toFixed(1)}%</td></tr>
          `).join('')}
        </tbody>
      </table>
      <h2>Top Products</h2>
      <table>
        <thead><tr><th>Rank</th><th>Product</th><th class="right">Units</th><th class="right">Revenue</th><th class="right">Margin %</th></tr></thead>
        <tbody>
          ${topProductsData.map(p => `
            <tr><td>${p.rank}</td><td>${p.name}</td><td class="right">${p.units}</td><td class="right">${formatAmount(p.revenue)}</td><td class="right">${p.margin.toFixed(1)}%</td></tr>
          `).join('')}
        </tbody>
      </table>
      <h2>Revenue by Region</h2>
      <table>
        <thead><tr><th>Region</th><th class="right">Revenue</th><th class="right">% of Total</th><th class="right">Growth %</th></tr></thead>
        <tbody>
          ${regionTableData.map(r => `
            <tr><td>${r.region}</td><td class="right">${formatAmount(r.revenue)}</td><td class="right">${r.pctOfTotal.toFixed(1)}%</td><td class="right">${r.growth.toFixed(1)}%</td></tr>
          `).join('')}
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const CountTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{entry.value}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Sales Intelligence Report" breadcrumb="Reports / Sale Reports / Sales Intelligence">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomer}
              onChange={setSelectedCustomer}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Group By</Label>
            <Select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={!hasSearched}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={!hasSearched}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadData} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color="#4A90D9"><FiShoppingCart /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Quotations</SummaryLabel>
              <SummaryValue style={{ color: '#4A90D9' }}>{summary.qtCount}</SummaryValue>
              <SummarySubValue>{formatCurrency(summary.qtValue)}</SummarySubValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Conversion Rate</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{summary.conversionRate.toFixed(1)}%</SummaryValue>
              <SummarySubValue>QT accepted / total QT</SummarySubValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Revenue</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalRevenue)}</SummaryValue>
              <SummarySubValue>Posted invoices</SummarySubValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#E67E22"><FiMapPin /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Top Region</SummaryLabel>
              <SummaryValue style={{ color: '#E67E22', fontSize: '16px' }}>{summary.topRegion}</SummaryValue>
              <SummarySubValue>{formatCurrency(summary.topRegionRevenue)}</SummarySubValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#9B59B6"><FiUsers /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Unique Customers</SummaryLabel>
              <SummaryValue style={{ color: '#9B59B6' }}>{summary.uniqueCustomers}</SummaryValue>
              <SummarySubValue>With posted invoices</SummarySubValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiBarChart2 /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Avg Order Value</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.avgOrderValue)}</SummaryValue>
              <SummarySubValue>Total SI / Count SI</SummarySubValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Funnel Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiTrendingUp /></EmptyIcon>
              <EmptyText>Loading sales intelligence data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiTrendingUp /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view sales intelligence</EmptyText>
            </EmptyState>
          ) : funnelData.stages[0].count === 0 && funnelData.stages[4].count === 0 ? (
            <EmptyState>
              <EmptyIcon><FiTrendingUp /></EmptyIcon>
              <EmptyText>No sales data found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <>
              <SectionTitle>Sales Funnel</SectionTitle>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Stage</Th>
                    <Th $right>Count</Th>
                    <Th $right>Value</Th>
                    <Th $right>Conversion %</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {funnelData.stages.map((s, index) => (
                    <Tr key={index}>
                      <Td><FunnelStageLabel $index={index}>{s.stage}</FunnelStageLabel></Td>
                      <Td $right>{s.count}</Td>
                      <Td $right>{formatAmount(s.value)}</Td>
                      <Td $right>{s.conversion.toFixed(1)}%</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </>
          )}
        </TableContainer>
      </ContentCard>

      {/* Top Products Table */}
      {hasSearched && !loading && topProductsData.length > 0 && (
        <ContentCard style={{ marginTop: 20 }}>
          <TableContainer>
            <SectionTitle>Top Products</SectionTitle>
            <Table>
              <Thead>
                <Tr>
                  <Th>Rank</Th>
                  <Th>Product</Th>
                  <Th $right>Units</Th>
                  <Th $right>Revenue</Th>
                  <Th $right>Margin %</Th>
                </Tr>
              </Thead>
              <Tbody>
                {topProductsData.map((p, index) => (
                  <Tr key={index}>
                    <Td>{p.rank}</Td>
                    <Td>{p.name}</Td>
                    <Td $right>{p.units}</Td>
                    <Td $right>{formatAmount(p.revenue)}</Td>
                    <Td $right>
                      <MarginBadge $positive={p.margin >= 0}>{p.margin.toFixed(1)}%</MarginBadge>
                    </Td>
                  </Tr>
                ))}
                <TotalsRow>
                  <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({topProductsData.length} products)
                  </Td>
                  <Td $right style={{ fontWeight: 700 }}>
                    {topProductsData.reduce((sum, p) => sum + p.units, 0)}
                  </Td>
                  <Td $right style={{ fontWeight: 700 }}>
                    {formatAmount(topProductsData.reduce((sum, p) => sum + p.revenue, 0))}
                  </Td>
                  <Td $right style={{ fontWeight: 700 }}>
                    {(() => {
                      const totalRev = topProductsData.reduce((sum, p) => sum + p.revenue, 0);
                      const totalCost = topProductsData.reduce((sum, p) => sum + (p.revenue - (p.revenue * p.margin / 100)), 0);
                      return totalRev > 0 ? (((totalRev - totalCost) / totalRev) * 100).toFixed(1) : '0.0';
                    })()}%
                  </Td>
                </TotalsRow>
              </Tbody>
            </Table>
          </TableContainer>
        </ContentCard>
      )}

      {/* Regions Table */}
      {hasSearched && !loading && regionTableData.length > 0 && (
        <ContentCard style={{ marginTop: 20 }}>
          <TableContainer>
            <SectionTitle>Revenue by Region</SectionTitle>
            <Table>
              <Thead>
                <Tr>
                  <Th>Region</Th>
                  <Th $right>Revenue</Th>
                  <Th $right>% of Total</Th>
                  <Th $right>Growth %</Th>
                </Tr>
              </Thead>
              <Tbody>
                {regionTableData.map((r, index) => (
                  <Tr key={index}>
                    <Td>{r.region}</Td>
                    <Td $right>{formatAmount(r.revenue)}</Td>
                    <Td $right>{r.pctOfTotal.toFixed(1)}%</Td>
                    <Td $right>{r.growth.toFixed(1)}%</Td>
                  </Tr>
                ))}
                <TotalsRow>
                  <Td style={{ fontWeight: 700 }}>
                    Total ({regionTableData.length} regions)
                  </Td>
                  <Td $right style={{ fontWeight: 700 }}>
                    {formatAmount(regionTableData.reduce((sum, r) => sum + r.revenue, 0))}
                  </Td>
                  <Td $right style={{ fontWeight: 700 }}>100.0%</Td>
                  <Td $right>-</Td>
                </TotalsRow>
              </Tbody>
            </Table>
          </TableContainer>
        </ContentCard>
      )}

      {/* Charts */}
      {hasSearched && !loading && filteredTransactions.length > 0 && (
        <ChartsRow>
          {/* Sales Funnel - Horizontal Bar Chart */}
          <ChartSection $wide>
            <ChartTitle>Sales Funnel</ChartTitle>
            {funnelChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={funnelChartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} stroke={colors.text.secondary} width={110} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="value" name="Value" fill={colors.chart.primary} radius={[0, 4, 4, 0]} barSize={24} />
                  <Bar dataKey="count" name="Count" fill={colors.chart.accent1} radius={[0, 4, 4, 0]} barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No funnel data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Revenue by Region - Vertical Bar Chart */}
          <ChartSection>
            <ChartTitle>Revenue by Region</ChartTitle>
            {regionChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={regionChartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="region" tick={{ fontSize: 10, angle: -30, textAnchor: 'end' }} stroke={colors.text.secondary} height={60} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" fill={colors.chart.secondary} radius={[4, 4, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No region data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Top Products - Horizontal Bar Chart */}
          <ChartSection>
            <ChartTitle>Top 10 Products by Revenue</ChartTitle>
            {topProductsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topProductsData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} stroke={colors.text.secondary} width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} barSize={20}>
                    {topProductsData.map((entry, index) => (
                      <rect key={`bar-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No product data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Customer Acquisition - Area Chart */}
          <ChartSection $wide>
            <ChartTitle>Customer Acquisition (Monthly New Customers)</ChartTitle>
            {acquisitionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={acquisitionData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="acqGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#9B59B6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#9B59B6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} allowDecimals={false} />
                  <Tooltip content={<CountTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="newCustomers" name="New Customers" stroke="#9B59B6" fill="url(#acqGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No acquisition data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummarySubValue = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
  padding: 16px 20px 0 20px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const FunnelStageLabel = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    const idx = props.$index || 0;
    const bgs = ['#E3F2FD', '#FFF3E0', '#E8F5E9', '#F3E5F5', '#FFEBEE'];
    return bgs[idx % bgs.length];
  }};
  color: ${props => {
    const idx = props.$index || 0;
    const cls = ['#1565C0', '#E65100', '#2E7D32', '#7B1FA2', '#C62828'];
    return cls[idx % cls.length];
  }};
`;

const MarginBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$positive ? '#E8F5E9' : '#FFEBEE'};
  color: ${props => props.$positive ? '#2E7D32' : '#C62828'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default SalesIntelligenceReport;
